import { useState, useEffect } from 'react';
import { Clock, AlertTriangle, FileDown, Mail, X, Send, Loader2, Cog, ScrollText, ShieldAlert, Star, Lightbulb } from 'lucide-react';
import { apiCall } from './useAutoSave';
import RecipeKatIcon from './RecipeKatIcon';
import { SECTION_COLOR } from './receptUtils';
import ZnalostiOdznak, { usePoctyZnalosti } from '../znalosti/ZnalostiOdznak';

const RED = '#EF4444';
const GREEN = '#22C55E';

// Poznámky ke kroku — stejná řeč jako v editoru (StepPostup), jen barvu tady
// nese celý blok, ať kuchař bezpečnostní poznámku najde na první pohled.
// Starší záznamy mají typ prázdný, proto fallback na „kvalita".
const POZNAMKA_TYP = {
    bezpecnost: { label: 'Bezpečnost', Icon: ShieldAlert, color: '#EF4444', rgb: '239,68,68' },
    kvalita:    { label: 'Kvalita',    Icon: Star,        color: '#F59E0B', rgb: '245,158,11' },
    zrucnost:   { label: 'Zručnost',   Icon: Lightbulb,   color: '#3B82F6', rgb: '59,130,246' },
};
const poznamkaCfg = (typ) => POZNAMKA_TYP[typ] || POZNAMKA_TYP.kvalita;

// Číslo v cs formátu bez zbytečných nul (2,50 → 2,5; 3,00 → 3)
const fmtMn = (v) => {
    if (v == null || isNaN(v)) return '';
    return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 3 }).format(Number(v));
};

const toast = (title, body) => window.dispatchEvent(new CustomEvent('in-app-toast', {
    detail: { id: crypto.randomUUID(), title, body },
}));

// Read-only krásně formátovaný recept + export PDF + odeslání mailem.
export default function RecipeReadView({ receptId, isDark, mutedColor, secondaryColor, isMobile }) {
    const [r, setR] = useState(null);
    const poctyZnalosti = usePoctyZnalosti('sop_krok', r?.kroky?.map(k => k.id));
    const [loading, setLoading] = useState(true);
    const [mailOpen, setMailOpen] = useState(false);
    const [mailTo, setMailTo] = useState('');
    const [mailMsg, setMailMsg] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        apiCall(`/api/recepty/recepty/${receptId}/recept`)
            .then(d => { if (alive) { setR(d); setLoading(false); } })
            .catch(() => { if (alive) { setR(null); setLoading(false); } });
        return () => { alive = false; };
    }, [receptId]);

    const exportPdf = () => window.open(`/api/recepty/recepty/${receptId}/recept-pdf`, '_blank');

    const sendMail = async () => {
        if (!mailTo.trim()) return;
        setSending(true);
        try {
            const res = await apiCall(`/api/recepty/recepty/${receptId}/recept-email`, 'POST', { email: mailTo.trim(), zprava: mailMsg.trim() || undefined });
            toast('Odesláno', res?.message || 'Recept byl odeslán.');
            setMailOpen(false); setMailMsg('');
        } catch (e) {
            toast('Chyba odeslání', e?.error || e?.message || 'Zkuste to znovu');
        }
        setSending(false);
    };

    const card = isDark
        ? { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }
        : { background: 'var(--surface-raised)', border: '1px solid var(--border-default)' };

    if (loading) {
        return <div className="flex items-center justify-center py-16" style={{ color: mutedColor }}><Loader2 size={20} className="animate-spin" /></div>;
    }
    if (!r) {
        return <div className="py-16 text-center text-sm" style={{ color: mutedColor }}>Recept se nepodařilo načíst.</div>;
    }

    const chip = (label, opts = {}) => (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999,
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
            background: opts.bg || (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
            color: opts.color || secondaryColor,
        }}>{opts.icon}{label}</span>
    );

    const sectionTitle = (text, color = SECTION_COLOR) => (
        <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color, margin: '18px 0 8px' }}>{text}</h3>
    );

    return (
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {/* Toolbar */}
            <div className="flex items-center justify-end gap-2 mb-4">
                <button onClick={exportPdf}
                    className="flex items-center gap-1.5 rounded-lg text-xs font-medium"
                    style={{ padding: '7px 12px', background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <FileDown size={14} /> Export PDF
                </button>
                <button onClick={() => setMailOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg text-xs font-medium"
                    style={{ padding: '7px 12px', background: SECTION_COLOR, border: 'none', color: '#fff', cursor: 'pointer' }}>
                    <Mail size={14} /> Odeslat mailem
                </button>
            </div>

            {/* Hero */}
            <div className="flex items-center gap-3 mb-3">
                <RecipeKatIcon foto={r.foto_url} ikona={r.kategorie?.ikona} kategorieNazev={r.kategorie?.nazev} kategorieBarva={r.kategorie?.barva} size={56} radius={9} />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{r.nazev}</h2>
                        {r.kod && <span className="text-[11px] font-mono px-2 py-0.5 rounded-full" style={{ background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.12)', color: SECTION_COLOR }}>{r.kod}</span>}
                    </div>
                    {r.varianta && <div className="text-xs mt-0.5" style={{ color: mutedColor }}>{r.varianta}</div>}
                </div>
            </div>

            {/* Chips */}
            <div className="flex flex-wrap gap-1.5 mb-1">
                {chip(r.typ === 'PRO' ? 'Produkt' : 'Polotovar', { bg: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.12)', color: SECTION_COLOR })}
                {r.kategorie && chip(r.kategorie.nazev)}
                {r.davka?.mnozstvi != null && chip(`Dávka: ${fmtMn(r.davka.mnozstvi)} ${r.davka.jednotka || ''}`)}
                {r.cas_pripravy_min != null && chip(`${r.cas_pripravy_min} min`, { icon: <Clock size={11} /> })}
            </div>

            {/* Ingredience */}
            {sectionTitle('Ingredience')}
            {r.ingredience.length > 0 ? (
                <div className="rounded-xl overflow-hidden" style={card}>
                    {r.ingredience.map((ing, i) => (
                        <div key={i} className="flex items-center" style={{ padding: '7px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: i < r.ingredience.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` : 'none' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: SECTION_COLOR, minWidth: 90, textAlign: 'right', marginRight: 16, flexShrink: 0 }}>{fmtMn(ing.spotreba)} {ing.jednotka}</span>
                            {ing.nazev}
                        </div>
                    ))}
                </div>
            ) : <p className="text-sm" style={{ color: mutedColor }}>Žádné ingredience.</p>}

            {/* Alergeny */}
            {sectionTitle('Alergeny', RED)}
            {r.alergeny.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {r.alergeny.map((a, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: 'rgba(239,68,68,0.12)', color: RED, border: '1px solid rgba(239,68,68,0.25)' }}>
                            {a.cislo_eu}. {a.nazev}
                        </span>
                    ))}
                </div>
            ) : <p className="text-sm" style={{ color: GREEN }}>Bez alergenů.</p>}

            {/* Postup */}
            {sectionTitle('Postup přípravy')}
            {r.kroky.length > 0 ? (
                <div className="flex flex-col gap-2.5">
                    {r.kroky.map((krok) => (
                        <div key={krok.id} className="rounded-xl p-3.5" style={card}>
                            <div className="flex items-start gap-3">
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: SECTION_COLOR, color: '#fff', fontSize: 13, fontWeight: 700 }}>{krok.krok}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{krok.nazev}</span>
                                        <ZnalostiOdznak typ="sop_krok" id={krok.id} pocet={poctyZnalosti[krok.id]} isDark={isDark} kompaktni />
                                        {krok.kriticky_bod && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600, background: 'rgba(239,68,68,0.15)', color: RED }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: RED }} /> Kritický bod
                                            </span>
                                        )}
                                    </div>
                                    {/* Meta: čas, stroje, operátoři */}
                                    {(() => {
                                        const strojeList = krok.stroje?.length ? krok.stroje : (krok.stroj ? [krok.stroj] : []);
                                        const lide = krok.pocet_operatoru
                                        // Nula se ukazuje taky — „bez obsluhy" je u kroku
                                        // se strojem podstatná informace, ne prázdno.
                                        const zajimavyPocet = lide != null && lide !== 1
                                        if (!(krok.cas_min || strojeList.length || zajimavyPocet)) return null;
                                        return (
                                        <div className="flex items-center gap-3 mt-1 flex-wrap" style={{ fontSize: 11, color: mutedColor }}>
                                            {krok.cas_min != null && <span className="inline-flex items-center gap-1"><Clock size={11} /> {krok.cas_min} min</span>}
                                            {strojeList.map((s, si) => <span key={s.id ?? si} className="inline-flex items-center gap-1" style={{ color: '#0CC0DF' }}><Cog size={11} /> {s.nazev}</span>)}
                                            {zajimavyPocet && <span>{lide === 0 ? 'bez obsluhy' : `${lide} os.`}</span>}
                                        </div>
                                        );
                                    })()}
                                    {krok.popis && <p style={{ fontSize: 13, color: secondaryColor, margin: '6px 0 0', lineHeight: 1.5 }}>{krok.popis}</p>}
                                    {krok.ingredience.length > 0 && (
                                        <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, marginTop: 8, paddingTop: 6 }}>
                                            {krok.ingredience.map((ki, j) => (
                                                <div key={j} className="flex items-center" style={{ fontSize: 12, padding: '2px 0', color: 'var(--text-primary)' }}>
                                                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: SECTION_COLOR, minWidth: 78, textAlign: 'right', marginRight: 12, flexShrink: 0 }}>{fmtMn(ki.spotreba)} {ki.jednotka}</span>
                                                    {ki.nazev}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {krok.poznamky?.length > 0 && (
                                        <div className="flex flex-col gap-1.5" style={{ marginTop: 8 }}>
                                            {krok.poznamky.map((p) => {
                                                const cfg = poznamkaCfg(p.typ);
                                                return (
                                                    <div key={p.id} className="flex items-start gap-2"
                                                        style={{
                                                            padding: '6px 10px', borderRadius: '0 8px 8px 0',
                                                            borderLeft: `2px solid ${cfg.color}`,
                                                            background: `rgba(${cfg.rgb},${isDark ? 0.10 : 0.07})`,
                                                        }}>
                                                        <cfg.Icon size={13} style={{ color: cfg.color, flexShrink: 0, marginTop: 1 }} />
                                                        <div className="min-w-0">
                                                            {/* Bez pre-wrap schválně: uložené texty jsou natvrdo
                                                                zalomené uprostřed vět, ať se přelijí podle šířky. */}
                                                            <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.45 }}>{p.text}</div>
                                                            <div style={{ fontSize: 10, color: mutedColor, marginTop: 1 }}>{cfg.label}</div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : <p className="text-sm" style={{ color: mutedColor }}>Postup není zadán.</p>}

            {/* Stroje */}
            {r.stroje.length > 0 && (
                <>
                    {sectionTitle('Stroje a náčiní', '#0CC0DF')}
                    <div className="rounded-xl p-3.5" style={card}>
                        {r.stroje.map((s, i) => (
                            <div key={i} className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--text-primary)', padding: '3px 0' }}>
                                <span style={{ color: '#0CC0DF' }}>•</span> {s.nazev}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Poznámka */}
            {r.poznamka && (
                <>
                    {sectionTitle('Poznámka')}
                    <div className="rounded-xl p-3.5" style={card}>
                        <p style={{ fontSize: 13, color: secondaryColor, whiteSpace: 'pre-wrap', margin: 0 }}>{r.poznamka}</p>
                    </div>
                </>
            )}

            {/* Revize footer */}
            {r.posledni_revize && (
                <p className="mt-4 text-[11px]" style={{ color: mutedColor }}>
                    Poslední revize: {r.posledni_revize.datum}{r.posledni_revize.uzivatel ? ` — ${r.posledni_revize.uzivatel}` : ''}
                </p>
            )}

            {/* Email modal */}
            {mailOpen && (
                <div onClick={() => !sending && setMailOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: '95vw', borderRadius: 12, overflow: 'hidden', background: isDark ? 'rgba(30,30,46,0.95)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}` }}>
                        <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><Mail size={15} style={{ color: SECTION_COLOR }} /> Odeslat recept mailem</h3>
                            <button onClick={() => setMailOpen(false)} disabled={sending} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}><X size={18} /></button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div>
                                <label className="text-[11px] font-medium block mb-1" style={{ color: secondaryColor }}>E-mail příjemce</label>
                                <input type="email" value={mailTo} onChange={e => setMailTo(e.target.value)} placeholder="jmeno@firma.cz" autoFocus
                                    className="w-full rounded-lg px-3 py-2 text-sm"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, color: 'var(--text-primary)', outline: 'none' }} />
                            </div>
                            <div>
                                <label className="text-[11px] font-medium block mb-1" style={{ color: secondaryColor }}>Zpráva (volitelná)</label>
                                <textarea value={mailMsg} onChange={e => setMailMsg(e.target.value)} rows={3} placeholder="Krátký doprovodný text…"
                                    className="w-full rounded-lg px-3 py-2 text-sm"
                                    style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, color: 'var(--text-primary)', outline: 'none', resize: 'vertical' }} />
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button onClick={() => setMailOpen(false)} disabled={sending}
                                    className="rounded-lg text-xs font-medium" style={{ padding: '7px 14px', background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, color: 'var(--text-primary)', cursor: 'pointer' }}>Zrušit</button>
                                <button onClick={sendMail} disabled={sending || !mailTo.trim()}
                                    className="flex items-center gap-1.5 rounded-lg text-xs font-medium" style={{ padding: '7px 14px', background: SECTION_COLOR, border: 'none', color: '#fff', cursor: 'pointer', opacity: (sending || !mailTo.trim()) ? 0.5 : 1 }}>
                                    {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Odeslat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
